import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Tv,
  Plus,
  Copy,
  Check,
  RefreshCw,
  Clock,
  MapPin,
  Trash2,
  KeyRound,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import ProvisionDisplayModal from '../../components/advertising/ProvisionDisplayModal';
import { RubyLoader } from '../../components/RubyLoader';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MarketingDisplay {
  displayId: string;
  tenantId: string;
  locationId: string;
  locationName?: string;
  name: string;
  description?: string;
  resolution?: '1080p' | '4k';
  orientation?: 'landscape' | 'portrait';
  provisionCode?: string;
  status: 'pending' | 'activated' | 'released';
  assignedPlaylistId?: string;
  assignedPlaylistIds?: string[];
  assignedPlaylistName?: string;
  createdAt: number;
  expiresAt?: number;
  activatedAt?: number;
  lastSeenAt?: number;
}

interface Props {
  tenantId: string;
  tenantName: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateTime = (ts?: number | null): string => {
  if (!ts) return 'N/A';
  const normalized = ts < 946684800000 ? ts * 1000 : ts;
  return new Date(normalized).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusPill = (status: string, isExpired: boolean) => {
  if (isExpired) return 'bg-red-100 text-red-800';
  switch (status) {
    case 'activated': return 'bg-emerald-100 text-emerald-800';
    case 'pending':   return 'bg-amber-100 text-amber-800';
    case 'released':  return 'bg-gray-200 text-gray-700';
    default:          return 'bg-gray-100 text-gray-700';
  }
};

const statusLabel = (status: string, isExpired: boolean) => {
  if (isExpired) return 'Expired';
  switch (status) {
    case 'activated': return 'Activated';
    case 'pending':   return 'Pending';
    case 'released':  return 'Released';
    default:          return status;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AdvertisingTab: React.FC<Props> = ({ tenantId, tenantName }) => {
  const [displays, setDisplays] = useState<MarketingDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MarketingDisplay | null>(null);
  const [confirmRelease, setConfirmRelease] = useState<MarketingDisplay | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  // Map of locationId -> human name, resolved from the location-billing
  // requests table. The displays endpoint only returns the raw locationId;
  // resolving the name client-side keeps the backend simple.
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});

  const fetchDisplays = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.advertising.listDisplays(tenantId);
      const list: MarketingDisplay[] = resp?.displays || [];
      setDisplays(list);
    } catch (err: any) {
      toastService.error('Failed to load displays: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchLocationNames = useCallback(async () => {
    try {
      const data: any = await api.locationBilling.listTenantRequests(tenantId);
      const next: Record<string, string> = {};
      for (const r of data?.requests || []) {
        if (r?.locationId) next[r.locationId] = r.name || r.legalEntityName || r.locationId;
      }
      setLocationNames(next);
    } catch (err) {
      console.warn('[AdvertisingTab] location name lookup failed (non-fatal)', err);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchDisplays();
    fetchLocationNames();
  }, [fetchDisplays, fetchLocationNames]);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRelease = async () => {
    if (!confirmRelease) return;
    setActionBusy(true);
    try {
      await api.advertising.releaseDisplay(confirmRelease.displayId, tenantId);
      toastService.success('Display released — new provisioning code generated');
      setConfirmRelease(null);
      fetchDisplays();
    } catch (err: any) {
      toastService.error('Release failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setActionBusy(true);
    try {
      await api.advertising.deleteDisplay(confirmDelete.displayId, tenantId);
      toastService.success('Display deleted');
      setConfirmDelete(null);
      fetchDisplays();
    } catch (err: any) {
      toastService.error('Delete failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionBusy(false);
    }
  };

  const activeCount = displays.length;

  return (
    <div className="space-y-4">
      {/* Page Header Standard (black bg, white icon, white button) */}
      <div className="bg-black text-white px-5 py-3 flex items-center justify-between rounded-2xl">
        <div className="flex items-center gap-3 min-w-0">
          <Tv className="w-5 h-5 text-white shrink-0" />
          <h2 className="text-base md:text-lg font-semibold truncate">Marketing Displays</h2>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/15 text-white">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchDisplays}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-white text-black hover:bg-stone-100 transition-colors"
          >
            <Plus size={14} /> New Display
          </button>
        </div>
      </div>

      {/* Tenant context line */}
      <p className="text-stone-500 text-xs px-1">
        Manage marketing displays for {tenantName}
      </p>

      {/* Display List */}
      {loading ? (
        <RubyLoader variant="section" />
      ) : displays.length === 0 ? (
        <div className="text-center py-12 text-stone-500 bg-white rounded-2xl border border-stone-200/60">
          <Tv size={32} className="mx-auto mb-2 opacity-50" />
          <p>No marketing displays provisioned yet</p>
          <p className="text-stone-400 text-xs mt-1">Click "New Display" to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Code / Last Seen</th>
                  <th className="px-4 py-3">Playlist</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {displays.map((d) => {
                  const isExpired = d.status === 'pending' && !!d.expiresAt && Date.now() > d.expiresAt;
                  return (
                    <tr key={d.displayId} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                            <Tv size={14} className="text-stone-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-stone-800 truncate">{d.name}</p>
                            {d.resolution && (
                              <p className="text-[11px] text-stone-500">
                                {d.resolution} · {d.orientation || 'landscape'}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-stone-700">
                        <div className="flex items-center gap-1.5">
                          <MapPin size={12} className="text-stone-400" />
                          <span className="truncate">{d.locationName || locationNames[d.locationId] || d.locationId}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusPill(d.status, isExpired)}`}>
                          {statusLabel(d.status, isExpired)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {d.status === 'pending' && d.provisionCode && !isExpired ? (
                          <button
                            onClick={() => handleCopyCode(d.provisionCode!)}
                            className="inline-flex items-center gap-2 px-2.5 py-1 bg-stone-50 border border-stone-200 rounded-lg hover:border-stone-300"
                            title="Copy code"
                          >
                            <span className="font-mono text-sm font-bold text-amber-600">{d.provisionCode}</span>
                            {copiedCode === d.provisionCode ? (
                              <Check size={12} className="text-emerald-600" />
                            ) : (
                              <Copy size={12} className="text-stone-400" />
                            )}
                          </button>
                        ) : d.status === 'activated' ? (
                          <div className="text-xs text-stone-600 flex items-center gap-1">
                            <Clock size={11} className="text-stone-400" />
                            {formatDateTime(d.lastSeenAt) || 'Never'}
                          </div>
                        ) : (
                          <span className="text-xs text-stone-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-stone-700 text-xs">
                        {(() => {
                          const count = Array.isArray(d.assignedPlaylistIds)
                            ? d.assignedPlaylistIds.length
                            : d.assignedPlaylistId
                            ? 1
                            : 0;
                          if (count === 0) return <span className="text-stone-400">Unassigned</span>;
                          if (count === 1)
                            return d.assignedPlaylistName || <span className="text-stone-400">1 playlist</span>;
                          return <span>{count} playlists</span>;
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          {d.status === 'activated' && (
                            <button
                              onClick={() => setConfirmRelease(d)}
                              className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"
                              title="Release (generate new code)"
                            >
                              <KeyRound size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmDelete(d)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                            title="Delete display"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Provision Modal */}
      {showModal && (
        <ProvisionDisplayModal
          tenantId={tenantId}
          onClose={() => setShowModal(false)}
          onCreated={() => {
            fetchDisplays();
          }}
        />
      )}

      {/* Confirm Release Modal */}
      {confirmRelease && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !actionBusy && setConfirmRelease(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <KeyRound size={18} className="text-amber-600" />
              </div>
              <h3 className="font-semibold text-stone-800">Release display?</h3>
            </div>
            <p className="text-sm text-stone-600 mb-5">
              This will disconnect <strong>{confirmRelease.name}</strong> and generate a fresh provisioning code.
              The current device will need to re-enter the new code.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmRelease(null)}
                disabled={actionBusy}
                className="px-3 py-2 rounded-lg text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRelease}
                disabled={actionBusy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {actionBusy ? <><RubyGemMark size={14} drawing color="currentColor" /> Releasing...</> : 'Release & Re-generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !actionBusy && setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <h3 className="font-semibold text-stone-800">Delete display?</h3>
            </div>
            <p className="text-sm text-stone-600 mb-5">
              Permanently delete <strong>{confirmDelete.name}</strong>?
              The device will lose access and this cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={actionBusy}
                className="px-3 py-2 rounded-lg text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionBusy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {actionBusy ? <><RubyGemMark size={14} drawing color="currentColor" /> Deleting...</> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisingTab;
